/**
 * ShipEntity.ts — The player-controlled Courier Ship for World 2 (space shmup).
 *
 * Extends BaseEntity per AGENTS.md. Owns position, velocity, and HP; the
 * fixed-step input → velocity → position pipeline lives in `shipPhysics.ts`
 * so the entity stays a thin data holder and the physics module is easy to
 * unit-test without instantiating the entity.
 *
 * Phase 4 — World 2: Starfield Drift.
 * See ADR-009 and ROADMAP.md Phase 4 "Space Engine".
 *
 * @module ShipEntity
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  SHIP_WIDTH,
  SHIP_HEIGHT,
  SHIP_MAX_HP,
  SHIP_INVULN_DURATION_S,
} from '../../constants/spaceConstants';
import { stepShipPhysics } from '../engine/shipPhysics';

/**
 * ShipEntity — Nova's Courier Ship. One instance per space-level session.
 */
export class ShipEntity extends BaseEntity {
  /** Current hit points (0 = destroyed). */
  public health: number;
  /**
   * Maximum hit points. Fixed at {@link SHIP_MAX_HP} — HUD reads this for
   * health-bar scaling, so it must not drift with the starting health value.
   */
  public readonly maxHealth: number = SHIP_MAX_HP;
  /**
   * Seconds of post-hit invulnerability remaining. While > 0, {@link takeDamage}
   * is a no-op so a cluster of overlapping hazards can't drain multiple HP in
   * a single tick. Decremented each fixed step by `shipDamage.tickShipIframes`.
   */
  public iframesRemaining = 0;

  /**
   * @param spawnX - Starting world-space X position (top-left of hitbox).
   * @param spawnY - Starting world-space Y position (top-left of hitbox).
   */
  constructor(spawnX: number, spawnY: number) {
    super(spawnX, spawnY, SHIP_WIDTH, SHIP_HEIGHT);
    this.health = SHIP_MAX_HP;
  }

  /**
   * Apply hazard damage. No-op while {@link iframesRemaining} > 0 or the
   * ship is already destroyed. On a successful hit, clamps `health` to
   * the [0, maxHealth] range and arms iframes for
   * {@link SHIP_INVULN_DURATION_S} seconds.
   *
   * DECISION: Returns a boolean so collision callers can branch on "did
   * this hit land?" without re-reading `health` — needed so a hazard
   * pool entry (mine, missile, projectile, asteroid) is only consumed
   * by the hit that actually drained HP, not by a no-op pass during
   * iframes.
   *
   * @param amount - Positive HP to remove. Non-positive values are ignored.
   * @returns true if the hit applied; false if iframes/dead/non-positive.
   */
  public takeDamage(amount: number): boolean {
    if (amount <= 0) return false;
    if (this.dead) return false;
    if (this.iframesRemaining > 0) return false;
    this.health = Math.max(0, this.health - amount);
    this.iframesRemaining = SHIP_INVULN_DURATION_S;
    if (this.health <= 0) {
      this.dead = true;
    }
    return true;
  }

  /**
   * Execute one fixed-step update.
   *
   * DECISION: Delegates to `stepShipPhysics` rather than inlining the logic so
   * the same step function can be invoked directly from the scene-level step
   * (`spaceStep.ts`) — keeps the integration testable without an entity
   * wrapper.
   *
   * Reads `spaceState.cameraX` so the viewport clamp follows the auto-scroll
   * camera. If no space scene is active, falls back to cameraX=0 so a stray
   * call from a test or future debug tool does not crash.
   *
   * @param dt    - Fixed timestep in seconds (always FIXED_STEP).
   * @param level - Current level geometry. Unused by the ship today; reserved
   *                for future asteroid / boss-arena bounds queries.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    stepShipPhysics(this, dt, spaceState.cameraX);
  }
}
